import React from "react";

import { createHashHistory } from "history";
import { SnackbarProvider } from "notistack";
import ReactDOM from "react-dom";
import { Toaster } from "react-hot-toast";
import { Provider } from "react-redux";
import { Router } from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";

import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";

import App from "./App";
import Auth0ProviderWithHistory from "./Auth0ProviderWithHistory";
import ThemeProvider from "./ThemeProvider";
import { APITokenProvider } from "./contexts/api-token-context";
import { AppProvider } from "./contexts/app-context";
import { UserProvider } from "./contexts/user-context";
import "./index.css";
import { persistor, store } from "./store";

const history = createHashHistory();

let sentryReportDialogActive = false;

if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    beforeBreadcrumb: (breadcrumb) =>
      breadcrumb.category === "ui.click" ? null : breadcrumb,
    beforeSend: (event) => {
      if (event.exception && !sentryReportDialogActive) {
        const state = store.getState();
        sentryReportDialogActive = true;
        Sentry.showReportDialog({
          eventId: event.event_id,
          user: state.user,
        });
      }
      return event;
    },
    dsn: process.env.REACT_APP_SENTRY_DSN,
    integrations: [
      new Integrations.BrowserTracing({
        routingInstrumentation: Sentry.reactRouterV5Instrumentation(history),
      }),
    ],
    maxBreadcrumbs: 500,
    release: process.env.REACT_APP_SENTRY_RELEASE,
    tracesSampleRate: 0.01,
  });

  // Fix for being unable to focus
  // https://gitlab.iglu.net/internal/portal/frontend/-/issues/138
  // https://github.com/getsentry/sentry-javascript/issues/2941
  (window as any).sentryEmbedCallback = (embed: any) => {
    const items = document.querySelectorAll('.MuiDialog-root [tabindex="-1"]');
    for (let i = 0; i < items.length; i++) {
      items[i].removeAttribute("tabindex");
    }

    const close = embed.close.bind(embed);
    embed.close = () => {
      sentryReportDialogActive = false;
      for (let i = 0; i < items.length; i++) {
        items[i].setAttribute("tabindex", "-1");
      }
      close();
    };
  };
}

ReactDOM.render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate loading={false} persistor={persistor}>
        <Router history={history}>
          <Auth0ProviderWithHistory>
            <APITokenProvider>
              <AppProvider>
                <UserProvider>
                  <ThemeProvider>
                    <SnackbarProvider>
                      <Toaster position="bottom-center" reverseOrder={false} />
                      <App />
                    </SnackbarProvider>
                  </ThemeProvider>
                </UserProvider>
              </AppProvider>
            </APITokenProvider>
          </Auth0ProviderWithHistory>
        </Router>
      </PersistGate>
    </Provider>
  </React.StrictMode>,
  document.getElementById("root")
);
